import { useContext, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { ApplicationContext } from '../../store/context';
import {
  ace_hooks,
  lock_hooks,
  useApplicationDetail,
  useApplicationStatuses,
  useLoanApplicationFromCustomer,
} from '@axo/shared/data-access/hooks';
import { ApplicantCard } from '../../components/ApplicantCard';
import { ApplicationCard } from '../../components/ApplicationCard';
import { ApplicationData } from '../../components/ApplicationData';
import styles from './ApplicationEditor.module.css';
import { CustomerHistoryCard } from '../../components/CustomerHistoryCard';
import { classNames } from '@axo/shared/util/dom';
import { CommunicationCard } from '../../components/CommunicationCard';
import { ApplicationActionBar } from '../../components/ApplicationActionBar';
import { CreditWarningBanner } from '../../components/CreditWarningBanner';
import { LoanApplicationEventsCard } from '../../components/LoanApplicationEventsCard';
import {
  DataAccessContext,
  useHasAnyOfRoles,
} from '@axo/shared/data-access/provider';
import { ErrorView } from '../../components/ErrorView';
import {
  KnownResourceType,
  useEventLogger,
} from '@axo/shared/data-access/event-log';
import {
  event_log,
  loan_application,
  LoanApplication,
  permissions,
  Product,
} from '@axo/shared/data-access/types';
import { EventCodeACE } from '../../components/EventLogWrapper/event-code';
import { BankPanel } from '../../components/BankPanel';
import { BrandLogo } from '../../components/BrandLogo/BrandLogo';
import { BrokerLogo } from '../../components/BrokerLogo';
import { ApplicationLink } from '../../components/ApplicationLink';
import { MarketCountryCode } from '@axo/shared/types';
import { NBACard } from '../../components/NBACard';
import { useShouldUseBuckets } from '../../hooks/utils/useShouldUseBuckets';
import { CollateralCard } from '../../components/CollateralCard/CollateralCard';
import { ETaxCard } from '../../components/ETaxCard/ETaxCard';
import { ApplicationConditionalWrapper } from '../../components/ConditionalWrappers/ApplicationConditionalWrapper';
import { EligibleProductsPanel } from '../../components/EligibleProductsPanel/EligibleProductsPanel';
import { resolveApplicationsList } from './utils/resolveApplicationsList';
import { isSubmittedActive } from './utils/isSubmittedActive';

export function ApplicationEditor() {
  const { dispatch, state } = useContext(ApplicationContext);

  const shouldUseBuckets = useShouldUseBuckets();

  const { applicationID } = useParams();
  if (!applicationID) throw new Error(`Missing param "applicationID"`);
  const { mutateAsync: lockMutation } = lock_hooks.useLockApplication();
  const dataAccessContext = useContext(DataAccessContext);
  const app = useApplicationDetail(applicationID, async (application) => {
    if (!state.openApplications[application.ID]) {
      dispatch({
        type: 'Open application',
        scope: {
          parentType: 'navigation',
          id: application.ID,
        },
        payload: {
          marketCountry: application.MarketCountry,
          humanReadableID: application.HumanReadableID ?? '',
        },
      });
      const currentLock = await lock_hooks.fetchApplicationLock(
        dataAccessContext,
        application.MarketCountry,
        applicationID,
      );

      if (!currentLock) {
        await lockMutation({
          applicationID,
          marketCountry: application.MarketCountry,
        });
      }
    }
  });

  const appTag: event_log.Resource = {
    Type: KnownResourceType.LoanApplication,
    ID: applicationID,
  };
  const log = useEventLogger(appTag);
  const logSent = useRef(false);

  const customerApplications = useLoanApplicationFromCustomer(
    app.data?.CustomerID ?? '',
    { offset: 0, limit: 100 },
    [loan_application.StatusGroup.Active],
  );

  const applicationStatuses = useApplicationStatuses();

  const canViewOtherApp = useHasAnyOfRoles([
    permissions.RoleName.Manager,
    permissions.RoleName.Sales,
    permissions.RoleName.SecuredSales,
    permissions.RoleName.Telemarketing,
  ]);
  const applicationsList = useMemo(() => {
    if (!app.data) return [];
    const statusMap = applicationStatuses.data;
    const others = statusMap
      ? (customerApplications.data ?? []).filter(
          (application: LoanApplication) =>
            isSubmittedActive(application, statusMap),
        )
      : [];
    return resolveApplicationsList(app.data, others);
  }, [app.data, customerApplications.data, applicationStatuses.data]);

  useEffect(() => {
    if (logSent.current) return;
    log(EventCodeACE.OpenApplication);
    logSent.current = true;
  }, [log, applicationID]);

  const addRecentlyOpenedApplication =
    ace_hooks.useAddRecentlyOpenedApplication();
  useEffect(() => {
    if (app.data === undefined) {
      return;
    }

    addRecentlyOpenedApplication.mutate({
      applicationID,
      marketCountry: app.data.MarketCountry,
      humanReadableID: app.data.HumanReadableID ?? '',
    });
  }, [applicationID, app.data?.HumanReadableID, app.data?.MarketCountry]);

  const opened = state.openApplications[applicationID];
  useEffect(() => {
    if (opened && state.marketCountry !== opened.marketCountry) {
      dispatch({
        scope: { parentType: 'marketCountry' },
        type: 'Set market country',
        payload: opened.marketCountry,
      });
    }
  }, [state.marketCountry, opened, dispatch]);

  if (!applicationID) return null;

  return (
    <div>
      {app.error ? (
        <div className={styles.error}>
          <ErrorView
            title="Something went wrong"
            description="Failed to load the application. Please contact your manager."
            onReload={() => window.location.reload()}
          />
        </div>
      ) : null}
      <div className={styles.container}>
        {opened && (
          <>
            <div className={styles.column}>
              <ApplicantCard applicationID={applicationID} />
              <ApplicationConditionalWrapper
                whitelist={[
                  {
                    marketCountry: [
                      MarketCountryCode.Norway,
                      MarketCountryCode.Finland,
                    ],
                    productType: [Product.UnsecuredLoan, Product.CreditCard],
                  },
                ]}
              >
                <EligibleProductsPanel applicationID={applicationID} />
              </ApplicationConditionalWrapper>
              {applicationsList.map((application, index) => {
                const isFirstInGroup =
                  index === 0 ||
                  applicationsList[index - 1].Product !== application.Product;
                return (
                <ApplicationCard
                  key={application.ID}
                  marketCountry={opened.marketCountry}
                  applicationID={application.ID}
                  product={application.Product as loan_application.Product}
                  isExpandedByDefault={isFirstInGroup}
                  link={
                    application.ID !== app.data?.ID &&
                    canViewOtherApp && (
                      <div>
                        <ApplicationLink
                          className={styles.manageLink}
                          marketCountry={
                            (application.MarketCountry as MarketCountryCode) ||
                            ''
                          }
                          humanReadableID={application.HumanReadableID || ''}
                          id={application.ID}
                        >
                          Manage Application
                        </ApplicationLink>
                      </div>
                    )
                  }
                />
                );
              })}
            </div>
            <div
              className={classNames(styles.column, styles.applicationDetails)}
            >
              <div
                className={classNames(
                  styles.brandBrokerCard,
                  'bp5-elevation-1',
                )}
              >
                {app.data?.Meta?.Brand && (
                  <h4>
                    This is an application under the brand:
                    <BrandLogo brand={app.data?.Meta?.Brand} fallback="-" />
                  </h4>
                )}
                <h4>
                  This application has been submitted to:
                  <BrokerLogo broker={app.data?.Meta?.Broker} fallback="-" />
                </h4>
              </div>
              <CreditWarningBanner applicationID={applicationID} />
              <ApplicationActionBar />
              {app.data && <BankPanel applicationID={applicationID} />}
              <ApplicationData
                key={`data-${applicationID}`}
                applicationID={applicationID}
              />
              <ApplicationConditionalWrapper
                whitelist={[
                  {
                    marketCountry: [MarketCountryCode.Denmark],
                  },
                ]}
              >
                <ETaxCard
                  key={`etax-${applicationID}`}
                  applicationID={applicationID}
                />
              </ApplicationConditionalWrapper>
              {app.data && (
                <CollateralCard
                  applicationID={applicationID}
                  key={`collateral-${applicationID}`}
                />
              )}
              {app.data && <CustomerHistoryCard application={app.data} />}
              {app.data && <LoanApplicationEventsCard application={app.data} />}
            </div>
            <div className={styles.column}>
              {shouldUseBuckets && <NBACard applicationID={applicationID} />}

              <CommunicationCard
                marketCountry={opened.marketCountry}
                applicationID={applicationID}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
